import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { Upload } from 'lucide-react';
import { authAPI } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const PersonalInfo = () => {
  const { user, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    bio: user?.bio || ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar || '');

  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setAvatarPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let avatarUrl = user.avatar;

      // Upload avatar if new file selected
      if (avatarFile) {
        const uploadResult = await authAPI.uploadProfileImage(avatarFile);
        avatarUrl = `${process.env.REACT_APP_BACKEND_URL}${uploadResult.url}`;
      }

      const updates = {
        ...formData,
        avatar: avatarUrl
      };

      updateUser(updates);

      toast({
        title: '¡Información actualizada!',
        description: 'Tus datos personales han sido guardados',
      });
    } catch (error) {
      console.error('Error updating personal info:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar la información',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
            <Card className="p-6">
              <h1 className="text-2xl font-bold mb-6">Informacion Personal</h1>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Avatar */}
                <div>
                  <Label>Foto de perfil</Label>
                  <div className="mt-2 flex items-center space-x-4">
                    <Avatar className="w-24 h-24">
                      <AvatarImage src={avatarPreview} />
                      <AvatarFallback>{user?.name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <Input
                        id="avatar"
                        type="file"
                        accept="image/*"
                        onChange={handleAvatarChange}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('avatar').click()}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Cambiar foto
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="name">Nombre completo</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>

                {/* Email */}
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    disabled
                    className="bg-gray-100"
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    El email no puede ser modificado
                  </p>
                </div>

                {/* Phone */}
                <div>
                  <Label htmlFor="phone">Teléfono (opcional)</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>

                {/* Bio */}
                <div>
                  <Label htmlFor="bio">Biografía (opcional)</Label>
                  <textarea
                    id="bio"
                    value={formData.bio}
                    onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md"
                    rows={4}
                    placeholder="Cuéntanos sobre ti..."
                  />
                </div>

                <Button
                  type="submit"
                  className="bg-zl-orange hover:bg-zl-orange-600"
                  disabled={loading}
                >
                  {loading ? 'Guardando...' : 'Guardar cambios'}
                </Button>
              </form>
            </Card>
    </div>
  );
};

export default PersonalInfo;
